/* eslint-disable @next/next/no-img-element */
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Input,
  InputAdornment,
  Typography,
} from "@mui/material";
import React from "react";
import { MdEmail } from "react-icons/md";
import { useForm } from "react-hook-form";
import { SiMinutemailer } from "react-icons/si";
import { useSnackbar } from "notistack";
import { emailRegex } from "../../src/constants";
import { useMutation } from "react-query";
import axios from "axios";
import { load as loadCaptcha } from "recaptcha-v3";

interface IFormData {
  email: string;
}

const Newsletter: React.FC = () => {
  const { register, handleSubmit, formState, reset } = useForm<IFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async (
      data: IFormData & {
        "g-recaptcha-response": string;
      }
    ) => {
      const res = await axios.post(
        "https://api.pivovarcaslav.cz/newsletter/subscribe.php",
        data
      );

      return res;
    },
  });

  const { enqueueSnackbar } = useSnackbar();

  const { errors } = formState;

  const onSubmit = async (data: IFormData) => {
    if (isLoading) return;

    try {
      const captcha = await loadCaptcha(process.env.NEXT_PUBLIC_CAPTCHA, {
        useRecaptchaNet: true,
        autoHideBadge: true,
      });
      const token = await captcha.execute("subscribeNewsletter");

      const result = await mutateAsync({
        ...data,
        "g-recaptcha-response": token,
      });

      if (result.status === 200) {
        enqueueSnackbar(
          "Děkujeme, úspěšně jste se přihlásili k odběru denní nabídky",
          {
            variant: "success",
            autoHideDuration: 10000,
          }
        );
        reset();
      } else
        enqueueSnackbar("Něco se pokazilo, zkuste to prosím později", {
          variant: "error",
        });
    } catch (e) {
      enqueueSnackbar("Něco se pokazilo, zkuste to prosím později", {
        variant: "error",
      });
    }
  };

  return (
    <Container sx={(theme) => ({ marginY: theme.spacing(8) })}>
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          alignItems: "center",
          [theme.breakpoints.down("sm")]: { display: "block" },
        })}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            component="h2"
            sx={(theme) => ({
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: theme.spacing(2),
            })}
          >
            Denní nabídka emailem?
          </Typography>
          <Typography sx={{ marginBottom: 3 }}>
            Každé ráno naleznete naší denní nabídku ve své emailové schránce.
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                alignItems: "baseline",
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "column",
                  alignItems: "center",
                  gap: theme.spacing(4),
                },
              })}
            >
              <FormControl
                variant="standard"
                error={!!errors.email}
                sx={(theme) => ({
                  marginRight: 4,
                  zIndex: 1,
                  maxWidth: "50%",
                  [theme.breakpoints.down("sm")]: { maxWidth: "75%" },
                })}
                fullWidth
              >
                <Input
                  startAdornment={
                    <InputAdornment position="start">
                      <MdEmail />
                    </InputAdornment>
                  }
                  placeholder="jan@novak.cz"
                  {...register("email", {
                    required: true,
                    pattern: emailRegex,
                    validate: (value) => value.match(emailRegex).length > 0,
                  })}
                />
                {!!errors.email && (
                  <FormHelperText error>
                    Zkontrolujte zadaný email
                  </FormHelperText>
                )}
              </FormControl>
              <Button
                variant="contained"
                endIcon={<SiMinutemailer />}
                disabled={isLoading}
                onClick={handleSubmit(onSubmit)}
              >
                Přihlásit k odběru
              </Button>
            </Box>
          </form>
        </Box>
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: { display: "none" },
          })}
        >
          <img
            src="/illu/newsletter2.svg"
            alt=""
            style={{ width: "100%" }}
            loading="lazy"
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Newsletter;
